/**
 * UsageSection — dedicated usage panel for the settings page.
 *
 * Shows resource usage (members, repositories) and periodic AI usage
 * (suggestions, enrichments, autofix PRs) with meters + period info.
 * All data config-driven from plans.ts + usageMeters queries.
 */
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import {
  PERIODIC_QUOTAS,
  QUOTA_LABELS,
  getQuota,
  type PlanId,
  type Quota,
} from '../../../convex/billing/plans';
import { PlanBadge } from './PlanBadge';
import { UsageMeter } from './UsageMeter';
import s from './billing.module.scss';

interface UsageSectionProps {
  plan: PlanId;
}

/** Static (non-periodic) resource quotas to display. */
const RESOURCE_QUOTAS: { quota: Quota; label: string }[] = [
  { quota: 'maxMembers', label: QUOTA_LABELS.maxMembers },
  { quota: 'maxProjects', label: QUOTA_LABELS.maxProjects },
];

function formatPeriod(): string {
  const now = new Date();
  return now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function UsageSection({ plan }: UsageSectionProps) {
  const { orgId, activeRole } = useOrgContext();
  const { projects } = useProjectContext();
  const isAdmin = activeRole === 'owner' || activeRole === 'admin';
  const members = useQuery(
    api.memberships.listByOrg,
    orgId ? { orgId } : 'skip',
  );
  const usageSummary = useQuery(
    api.billing.usage.getUsageSummary,
    orgId && isAdmin ? { orgId } : 'skip',
  );

  const memberCount = members?.length ?? 0;
  const repositoryCount = projects.length;

  /** Map resource quotas to their live counts. */
  const resourceCounts: Record<string, number> = {
    maxMembers: memberCount,
    maxProjects: repositoryCount,
  };

  return (
    <div className={s.billingPage}>
      {/* ── Current plan context ── */}
      <div className={s.usagePlanBar}>
        <span className={s.usagePlanBarLabel}>Current plan</span>
        <PlanBadge plan={plan} />
      </div>

      {/* ── AI usage (periodic) ── */}
      <div className={s.billingSection}>
        <div className={s.usageSectionHeader}>
          <p className={s.billingSectionTitle}>AI usage</p>
          <span className={s.usagePeriodBadge}>{formatPeriod()}</span>
        </div>
        <p className={s.usageSectionDesc}>
          AI-powered features consume monthly quota that resets each billing
          period.
        </p>
        <div className={s.usageGrid}>
          {usageSummary?.map((meter) => (
            <UsageMeter
              key={meter.quota}
              label={meter.label}
              current={meter.current}
              limit={meter.limit}
            />
          )) ??
            PERIODIC_QUOTAS.map((quota) => (
              <UsageMeter
                key={quota}
                label={QUOTA_LABELS[quota]}
                current={0}
                limit={getQuota(plan, quota)}
              />
            ))}
        </div>
      </div>

      {/* ── Resource usage (static) ── */}
      <div className={s.billingSection}>
        <p className={s.billingSectionTitle}>Resources</p>
        <div className={s.usageGrid}>
          {RESOURCE_QUOTAS.map(({ quota, label }) => (
            <UsageMeter
              key={quota}
              label={label}
              current={resourceCounts[quota] ?? 0}
              limit={getQuota(plan, quota)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
